import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { peopleData } from '../data/peopleData';

const PeoplePage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-900 to-green-700 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Our People
            </h1>
            <p className="text-2xl text-green-100 leading-relaxed">
              Meet the team driving research, education, and community engagement in Highland Asia
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xl text-gray-700 leading-relaxed">
              The Highland Institute brings together scholars, researchers, and practitioners committed to 
              interdisciplinary inquiry, community-engaged scholarship, and the advancement of knowledge 
              rooted in Highland Asia. Our team includes full-time staff, research assistants, and a network 
              of fellows and visiting scholars from around the world.
            </p>
          </div>
        </div>
      </section>

      {/* Leadership Council */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Leadership Council</h2>
            <div className="h-1 w-24 bg-green-600"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12 items-stretch">
            {peopleData.leadership.map((person, index) => (
              <Link key={index} to={`/people/${person.id}`} className="block h-full">
                <Card className="h-full hover:shadow-xl transition-shadow hover:border-green-500 cursor-pointer">
                  <CardContent className="p-8 h-full">
                    <div className="flex flex-col items-center text-center h-full">
                      <div className="w-32 h-32 rounded-full overflow-hidden mb-4 bg-gray-200 flex-shrink-0">
                        <img 
                          src={person.image} 
                          alt={person.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-2 hover:text-green-600 transition-colors">
                        {person.honorific && <span>{person.honorific} </span>}
                        {person.name}
                      </h3>
                      <p className="text-green-600 font-medium mb-2">{person.title}</p>
                      <p className="text-sm text-gray-600">{person.affiliation}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {/* Honorary Vice Presidents */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Honorary Vice Presidents</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {peopleData.honoryVPs.map((person, index) => (
                <Link key={index} to={`/people/${person.id}`}>
                  <Card className="hover:shadow-lg transition-shadow hover:border-green-500 cursor-pointer">
                    <CardContent className="p-6">
                      <h4 className="text-lg font-bold text-gray-900 mb-2 hover:text-green-600 transition-colors">
                        {person.honorific && <span>{person.honorific} </span>}
                        {person.name}
                      </h4>
                      <p className="text-sm text-gray-600">{person.affiliation}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Senior Staff */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Senior Staff</h2>
            <div className="h-1 w-24 bg-green-600"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {peopleData.staff.map((person, index) => (
              <Link key={index} to={`/people/${person.id}`}>
                <Card className="hover:shadow-xl transition-shadow hover:border-green-500 cursor-pointer h-full">
                  <CardContent className="p-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-24 h-24 rounded-full overflow-hidden mb-4 bg-gray-200">
                        <img 
                          src={person.image} 
                          alt={person.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2 hover:text-green-600 transition-colors">
                        {person.honorific && <span>{person.honorific} </span>}
                        {person.name}
                      </h3>
                      <p className="text-sm text-green-600 font-medium">{person.title}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Research Staff */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Research Staff</h2>
            <div className="h-1 w-24 bg-green-600"></div>
            <p className="text-gray-600 mt-4">
              Our research staff play a central role in the Institute's work across all research themes, 
              supporting fieldwork, data collection, project coordination, documentation, and community engagement.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {peopleData.researchAssistants.map((person, index) => (
              <Link key={index} to={`/people/${person.id}`}>
                <Card className="hover:shadow-lg transition-shadow hover:border-green-500 cursor-pointer">
                  <CardContent className="p-4">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-20 h-20 rounded-full overflow-hidden mb-3 bg-gray-200">
                        <img 
                          src={person.image} 
                          alt={person.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h4 className="text-sm font-bold text-gray-900 mb-1 hover:text-green-600 transition-colors">{person.name}</h4>
                      <p className="text-xs text-gray-600">{person.title}</p>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Fellows & Visiting Scholars */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Fellows & Visiting Scholars</h2>
            <div className="prose prose-lg max-w-none mb-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                The <strong>Highland Fellowship</strong> is a network of scholars, researchers, and practitioners 
                who have been nominated in recognition of their sustained contributions to the Institute's mission 
                and vision. Fellows play an active role in mentoring students, leading research initiatives, and 
                shaping the Institute's programmes and events.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The Institute also welcomes <strong>Visiting Scholars</strong> from around the world, including 
                early-career academics, doctoral candidates, and independent researchers. Visiting scholars may 
                access our library and facilities, participate in the Institute's academic life, and present their work.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="https://highlandinstitute.org/fellowship-and-visiting-scholars/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="bg-green-600 hover:bg-green-700 px-8 py-6 text-lg">
                  Learn About Fellowship Programme
                  <ExternalLink className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Visiting and Collaborative Researchers */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Visiting and Collaborative Researchers</h2>
            <div className="prose prose-lg max-w-none mb-8">
              <p className="text-gray-700 leading-relaxed">
                The Highland Institute hosts <strong>Visiting and Collaborative Researchers</strong> who undertake 
                research in partnership with the Institute. These researchers include doctoral candidates, 
                postdoctoral fellows, and independent scholars whose work aligns with the Institute's focus on 
                highland societies, borderlands, and community-engaged scholarship.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {peopleData.visitingResearchers.map((person, index) => (
                <Link key={index} to={`/people/${person.id}`}>
                  <Card className="hover:shadow-xl transition-shadow hover:border-green-500 cursor-pointer h-full">
                    <CardContent className="p-6">
                      <div className="flex flex-col items-center text-center">
                        <div className="w-28 h-28 rounded-full overflow-hidden mb-4 bg-gray-200">
                          <img 
                            src={person.image} 
                            alt={person.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2 hover:text-green-600 transition-colors">
                          {person.honorific && <span>{person.honorific} </span>}
                          {person.name}
                        </h3>
                        <p className="text-sm text-green-600 font-medium mb-2">{person.title}</p>
                        <p className="text-xs text-gray-500">{person.affiliation}</p>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Join Us */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Join Our Team</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Interested in working with us or becoming a visiting scholar?
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/programmes">
              <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Study With Us
              </Button>
            </Link>
            <a href="mailto:info@highlandinstitute.org">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Contact Us
                <Mail className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PeoplePage;
